import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia, LinearProgress,
  Typography, Alert,
} from '@mui/material';
import {
  ResponsiveContainer,
} from 'recharts';
import styled from 'styled-components';
import { AppContext } from '../../providers/authProvider';
import { checkTaskPromise } from '../../api/checktask';
import api from '../../api/req';
import { BarPlot, PiePlot } from './plots';
import { colors } from './plots/consts';

const StyledCardMedia = styled(CardMedia)`
  height: 50vh;
`;
function Diagram({
  title,
  id,
  options,
}) {
  const { auth } = useContext(AppContext);
  const [data, setData] = useState(null);
  const [header, setHeader] = useState(title);
  const [size, setSize] = useState({
    width: 300,
    height: 300,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const loader = useCallback(
    async () => {
      const {
        params,
        filters,
        fields,
        group,
        order,
      } = options;
      const r = await api.post('/api/reports/render/', auth, {
        details: false,
        options: {
          fields,
          column: [],
          group,
          order,
          filter: filters,
        },
        params,
        render: 'json',
        report: id,
      });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      // eslint-disable-next-line camelcase
      const { task_id } = await r.json();
      const taskData = await checkTaskPromise(task_id, auth);
      if (taskData.result.errors) throw new Error(taskData.result.errors.join(', '));
      return taskData.result;
    },
    [auth, id, options],
  );

  useEffect(
    () => {
      setErr(null);
      setLoading(true);
      loader()
        .then((result) => {
          if (options.group.length === 0) throw new Error('В отчете должна быть как минимум одна группировка');
          const lineXname = options.group[0][0].key;
          const lineNames = result.meta.options.fields.filter((ff) => !!ff[1])
            .map((ff) => ff[0]);
          const getDataFromName = (dataRow, key) => dataRow
            .filter((item) => item.key === key)
            .reduce((R, item) => item.data, {});
          const dd = result.data.body.map((dataRow) => lineNames.reduce((R, key) => ({
            ...R,
            [key]: getDataFromName(dataRow, key)[key],
          }), {
            [lineXname]: getDataFromName(dataRow, lineXname).repr,
          }));
          setData({
            dataKey: {
              key: lineXname,
              label: result.meta.schema.fields[lineXname].label,
            },
            lines: lineNames.map((l, i) => ({
              name: l,
              color: colors[Math.min(i, colors.length - 1)],
              label: result.meta.schema.fields[l].label,
            })),
            items: dd,
            dType: lineNames.length === 1 ? 'PIE' : 'BAR',
          });
          setHeader(result.header);
        })
        .catch((e) => setErr(e.message))
        .finally(() => setLoading(false));
    },
    [loader, options.group],
  );

  const measuredRef = useCallback((node) => {
    if (node !== null) {
      const gbc = node.getBoundingClientRect();
      setSize({
        width: gbc.width,
        height: gbc.height,
      });
    }
  }, []);
  return (
    <Card>
      <CardContent>
        <Typography gutterBottom variant="h5" component="h5">
          {header}
        </Typography>
      </CardContent>
      <CardActionArea>
        <StyledCardMedia
          component="span"
          ref={measuredRef}
        >
          {loading && (
            <LinearProgress />
          )}
          {err && (
            <Alert severity="error">
              {err}
            </Alert>
          )}
          {data && (
            <ResponsiveContainer>
              <>
                {data.dType === 'BAR' && (
                  <BarPlot data={data} size={size} />
                )}
                {data.dType === 'PIE' && (
                  <PiePlot data={data} size={size} />
                )}
              </>
            </ResponsiveContainer>
          )}
        </StyledCardMedia>
      </CardActionArea>
    </Card>
  );
}

Diagram.propTypes = {
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  options: PropTypes.shape({
    params: PropTypes.shape({}),
    group: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          useHierarchy: PropTypes.bool,
        }),
      ),
    ),
    order: PropTypes.arrayOf(
      PropTypes.arrayOf(PropTypes.string),
    ),
    filters: PropTypes.arrayOf(
      PropTypes.arrayOf(PropTypes.any),
    ),
    fields: PropTypes.arrayOf(
      PropTypes.arrayOf(PropTypes.any),
    ),
  }).isRequired,
};
export default Diagram;
